import type { DocumentType } from '../../types/tuition-assistance.ts';

/**
 * Shared AI document-validation evaluation, extracted from the employee
 * categorized-upload component so the admin approval drawers can produce the
 * exact same verdict tags. The AI agent broadcasts its analysis once over the
 * ServiceResponseChannel; whichever surface uploaded the document evaluates the
 * result against the declared type and persists the verdict onto
 * `digital_asset.tags` (the backend never writes these tags).
 */

/** Verdicts this evaluator can produce for a completed analysis. */
export type AiValidationVerdict = 'valid' | 'flagged' | 'not_validated';
export type AiValidationFlagKind = 'content' | 'invalid_type';

/** Shape of the ActionCable broadcast from the AI agent. */
export interface ServiceResponsePayload {
  status?: string;
  data?: { summary?: unknown };
}

export interface AiValidationContext {
  /** The document type the uploader declared (from the configured row). */
  declaredType: DocumentType;
  /** The row key/label the upload was filed under; persisted for row matching. */
  uploadRowKey: string;
  /** Employee full name for identity verification against AI-detected names. */
  employeeName: string;
  expectedInstitutionName: string | null;
  expectedCourseNames: string[];
  /** i18n lookup — pass `(key, options) => intl.t(key, options)`. */
  translate: (key: string, options?: Record<string, string>) => string;
}

export interface AiValidationOutcome {
  status: AiValidationVerdict;
  feedback: string | null;
  flagKind?: AiValidationFlagKind;
  tags: Record<string, unknown>;
}

/**
 * Time the UI waits for the AI ServiceResponse before showing the "still
 * processing" fallback. The asset itself is already saved at this point — we
 * just stop spinning indefinitely.
 */
export const ANALYSIS_TIMEOUT_MS = 30_000;

const PRODUCTION_FILE_ACCEPT = '.jpg,.jpeg,.png,.pdf';
const NON_PRODUCTION_FILE_ACCEPT = `${PRODUCTION_FILE_ACCEPT},.heic,.heif`;
const PRODUCTION_FILE_TYPES_DISPLAY = 'PDF, PNG, JPG, JPEG';
const NON_PRODUCTION_FILE_TYPES_DISPLAY = `${PRODUCTION_FILE_TYPES_DISPLAY}, HEIC, HEIF`;

/**
 * Returns the TA attachment picker filter for a deployment environment.
 * Production omits raw HEIC/HEIF because reviewer browser support is not
 * portable. JPEG comes first so WebKit converts selected HEIC photos to JPEG.
 * Non-production environments retain the original formats for pipeline testing.
 *
 * @param environment - Deployment environment reported by FeaturesService.
 * @returns The comma-separated value for an input's `accept` attribute.
 */
export function aiReadableFileAccept(environment: string): string {
  return environment === 'production' ? PRODUCTION_FILE_ACCEPT : NON_PRODUCTION_FILE_ACCEPT;
}

/**
 * Returns the human-readable counterpart to {@link aiReadableFileAccept}.
 *
 * @param environment - Deployment environment reported by FeaturesService.
 * @returns File types displayed beside TA upload controls.
 */
export function aiReadableFileTypesDisplay(environment: string): string {
  return environment === 'production'
    ? PRODUCTION_FILE_TYPES_DISPLAY
    : NON_PRODUCTION_FILE_TYPES_DISPLAY;
}

/**
 * Max size for a single TA attachment upload. Enforced client-side by
 * FileUploader (`@maxFileSize`) on every TA upload surface — oversized files
 * are rejected before the S3 upload starts.
 */
export const TA_MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024;

type RequiredEvidenceKey =
  | 'institution'
  | 'payment_date'
  | 'payment_amount'
  | 'paid_items'
  | 'payment_method'
  | 'course_name'
  | 'grade'
  | 'completion_date'
  | 'credential_name';

function asRecord(value: unknown) {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function textValue(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function numberValue(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function arrayOfRecords(value: unknown) {
  return Array.isArray(value)
    ? value.map((item) => asRecord(item)).filter((item) => item !== null)
    : [];
}

/**
 * Unwrap the AI analysis from an asset-summary payload. The response wraps the
 * Gemini result inside a JSONAPI resource:
 *   summary = { id, type, attributes: { summary: { result: { ...ai fields } } } }
 * Falls back to the top-level summary in case the payload arrives pre-flattened.
 */
export function extractAiResult(summary: unknown) {
  const summaryRecord = asRecord(summary);
  if (!summaryRecord) return null;

  const attributes = asRecord(summaryRecord['attributes']);
  const summaryPayload = asRecord(attributes?.['summary']) ?? summaryRecord;
  return asRecord(summaryPayload['result']) ?? summaryPayload;
}

function comparableWords(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2 && !['the', 'and', 'for', 'inc', 'llc'].includes(word));
}

function textMatchesExpected(extracted: string, expected: string) {
  const extractedWords = new Set(comparableWords(extracted));
  const expectedWords = comparableWords(expected);
  if (expectedWords.length === 0 || extractedWords.size === 0) return false;
  return expectedWords.every((word) => extractedWords.has(word));
}

/** Case-insensitive check that every part of the employee name appears in the detected name. */
export function namesMatch(employeeName: string, detectedName: string) {
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z\s]/g, '')
      .trim();
  const employeeParts = normalize(employeeName).split(/\s+/).filter(Boolean);
  const detected = normalize(detectedName);
  return employeeParts.length > 0 && employeeParts.every((part) => detected.includes(part));
}

function missingEvidenceMessage(field: RequiredEvidenceKey, context: AiValidationContext) {
  return context.translate('tas.attachments.flag_reasons.missing_required_evidence', {
    field: context.translate(`tas.attachments.required_evidence.${field}`),
  });
}

function maybeAddInstitutionFlags(
  flags: string[],
  extractedInstitution: string | null,
  context: AiValidationContext
) {
  const expectedInstitution = textValue(context.expectedInstitutionName);
  if (!extractedInstitution) {
    flags.push(missingEvidenceMessage('institution', context));
    return;
  }

  if (
    expectedInstitution &&
    !textMatchesExpected(extractedInstitution, expectedInstitution) &&
    !textMatchesExpected(expectedInstitution, extractedInstitution)
  ) {
    flags.push(
      context.translate('tas.attachments.flag_reasons.institution_mismatch', {
        detected: extractedInstitution,
        expected: expectedInstitution,
      })
    );
  }
}

function addReceiptEvidenceFlags(
  flags: string[],
  aiResult: Record<string, unknown>,
  context: AiValidationContext
) {
  const receipt = asRecord(aiResult['receipt']) ?? {};
  const institution = textValue(receipt['entity']);
  const lineItems = arrayOfRecords(receipt['line_items'] ?? receipt['lineItems']);
  const paymentLines = lineItems.filter((lineItem) =>
    ['payment', 'credit'].includes(textValue(lineItem['line_type'] ?? lineItem['lineType']) ?? '')
  );
  const evidenceLines = paymentLines.length > 0 ? paymentLines : lineItems;

  maybeAddInstitutionFlags(flags, institution, context);
  if (!evidenceLines.some((lineItem) => textValue(lineItem['line_date'] ?? lineItem['lineDate']))) {
    flags.push(missingEvidenceMessage('payment_date', context));
  }
  if (
    !evidenceLines.some(
      (lineItem) => numberValue(lineItem['line_amount'] ?? lineItem['lineAmount']) !== null
    )
  ) {
    flags.push(missingEvidenceMessage('payment_amount', context));
  }
  if (
    !lineItems.some((lineItem) =>
      textValue(
        lineItem['line_item'] ??
          lineItem['lineItem'] ??
          lineItem['line_details'] ??
          lineItem['lineDetails']
      )
    )
  ) {
    flags.push(missingEvidenceMessage('paid_items', context));
  }
  if (
    !evidenceLines.some((lineItem) =>
      textValue(lineItem['form_of_payment'] ?? lineItem['formOfPayment'])
    )
  ) {
    flags.push(missingEvidenceMessage('payment_method', context));
  }
}

function addGradeEvidenceFlags(
  flags: string[],
  aiResult: Record<string, unknown>,
  context: AiValidationContext
) {
  const gradeOrTranscript = asRecord(aiResult['grade'] ?? aiResult['transcript']) ?? {};
  const institution = textValue(gradeOrTranscript['institution']);
  const courses = arrayOfRecords(gradeOrTranscript['courses']);
  const documentCourseNames = courses
    .map((course) =>
      textValue(
        course['course_name'] ??
          course['courseName'] ??
          course['course_detail'] ??
          course['courseDetail'] ??
          course['course']
      )
    )
    .filter((courseName) => courseName !== null);
  const expectedCourseNames = context.expectedCourseNames;

  maybeAddInstitutionFlags(flags, institution, context);
  if (documentCourseNames.length === 0) {
    flags.push(missingEvidenceMessage('course_name', context));
  } else if (
    expectedCourseNames.length > 0 &&
    !expectedCourseNames.some((expectedCourseName) =>
      documentCourseNames.some(
        (documentCourseName) =>
          textMatchesExpected(documentCourseName, expectedCourseName) ||
          textMatchesExpected(expectedCourseName, documentCourseName)
      )
    )
  ) {
    flags.push(
      context.translate('tas.attachments.flag_reasons.course_mismatch', {
        detected: documentCourseNames.join(', '),
        expected: expectedCourseNames.join(', '),
      })
    );
  }
  if (!courses.some((course) => textValue(course['grade']))) {
    flags.push(missingEvidenceMessage('grade', context));
  }
}

function addCertificateEvidenceFlags(
  flags: string[],
  aiResult: Record<string, unknown>,
  context: AiValidationContext
) {
  const certificate = asRecord(aiResult['certificate']) ?? {};
  const institution = textValue(certificate['institution']);
  const credentialName = textValue(
    certificate['degree_or_certification'] ?? certificate['degreeOrCertification']
  );

  maybeAddInstitutionFlags(flags, institution, context);
  if (!textValue(certificate['completion_date'] ?? certificate['completionDate'])) {
    flags.push(missingEvidenceMessage('completion_date', context));
  }
  if (!credentialName) {
    flags.push(missingEvidenceMessage('credential_name', context));
  }
}

function addRequiredEvidenceFlags(
  flags: string[],
  declaredType: DocumentType,
  aiResult: Record<string, unknown>,
  context: AiValidationContext
) {
  if (declaredType === 'receipt') {
    addReceiptEvidenceFlags(flags, aiResult, context);
  } else if (declaredType === 'grade' || declaredType === 'transcript') {
    addGradeEvidenceFlags(flags, aiResult, context);
  } else if (declaredType === 'certificate') {
    addCertificateEvidenceFlags(flags, aiResult, context);
  }
}

/**
 * Evaluate an unwrapped AI result against the declared document type and
 * produce the verdict plus the full tag payload to persist on the asset.
 */
export function evaluateAiValidation(
  aiResult: Record<string, unknown>,
  context: AiValidationContext
): AiValidationOutcome {
  const detectedTypesValue =
    aiResult['detectedDocumentTypes'] ?? aiResult['detected_document_types'];
  // External payload — a non-array here (string, object) must not throw, or
  // no fallback verdict would ever be persisted for the asset.
  const detectedTypes = Array.isArray(detectedTypesValue)
    ? (detectedTypesValue as DocumentType[])
    : [];
  const identity = asRecord(aiResult['identityCheck'] ?? aiResult['identity_check']) ?? {};
  const identityNameValue = identity['detectedName'] ?? identity['detected_name'];
  const identityName = typeof identityNameValue === 'string' ? identityNameValue : null;
  const identityConfidenceValue = identity['confidence'];
  const identityConfidence =
    typeof identityConfidenceValue === 'number' ? identityConfidenceValue : 0;
  const confidenceScoreValue = aiResult['confidenceScore'] ?? aiResult['confidence_score'];
  const confidenceScore = typeof confidenceScoreValue === 'number' ? confidenceScoreValue : 0;

  const declaredType = context.declaredType;
  const typeMatches =
    detectedTypes.includes(declaredType) ||
    (declaredType === 'grade' && detectedTypes.includes('transcript'));

  const isUnreadable = (detectedTypes as string[]).includes('unreadable');
  const isUnclassifiable =
    !isUnreadable && detectedTypes.length > 0 && detectedTypes.every((d) => d === 'general');
  const employeeName = context.employeeName;
  const nameMatches =
    identityConfidence >= 0.5 &&
    typeof employeeName === 'string' &&
    namesMatch(employeeName, identityName ?? '');
  const identityOk = nameMatches;

  const flags: string[] = [];
  let flagKind: AiValidationFlagKind = 'content';

  if (isUnreadable) {
    flags.push(context.translate('tas.attachments.flag_reasons.unreadable'));
    flagKind = 'invalid_type';
  } else if (!typeMatches && !isUnclassifiable) {
    const detectedLabel = detectedTypes.join(' + ') || 'general';
    flags.push(
      context.translate('tas.attachments.flag_reasons.type_mismatch', {
        detected: detectedLabel,
        expected: declaredType,
      })
    );
  }
  if (!identityOk && !isUnclassifiable) {
    if (identityName && employeeName && !nameMatches) {
      flags.push(
        context.translate('tas.attachments.flag_reasons.name_mismatch', {
          detected: identityName,
          expected: employeeName,
        })
      );
    } else {
      flags.push(context.translate('tas.attachments.flag_reasons.name_unverified'));
    }
  }
  if (confidenceScore > 0 && confidenceScore <= 2) {
    flags.push(context.translate('tas.attachments.flag_reasons.low_confidence'));
  }
  if (!isUnreadable && !isUnclassifiable && typeMatches) {
    addRequiredEvidenceFlags(flags, declaredType, aiResult, context);
  }

  let status: AiValidationVerdict;
  if (isUnclassifiable) {
    status = 'not_validated';
  } else if (flags.length > 0) {
    status = 'flagged';
  } else {
    status = 'valid';
  }

  const feedback = flags.length > 0 ? flags.join('. ') : null;

  return {
    status,
    flagKind: status === 'flagged' ? flagKind : undefined,
    feedback,
    tags: {
      uploadRowKey: context.uploadRowKey,
      employeeSelectedType: declaredType,
      aiDetectedTypes: detectedTypes,
      aiValidationStatus: status === 'not_validated' ? 'flagged' : status,
      aiValidationDisplayStatus: status,
      aiValidationFeedback: feedback,
      aiTypeMatch: String(typeMatches),
      aiIdentityMatch: String(identityOk),
    },
  };
}

/** Tags to persist when the AI response arrived without a usable analysis. */
export function analysisErrorTags(
  baseTags: Record<string, unknown> | null | undefined,
  declaredType: DocumentType,
  translate?: AiValidationContext['translate']
): Record<string, unknown> {
  return {
    ...(baseTags ?? {}),
    employeeSelectedType: declaredType,
    aiValidationStatus: 'error',
    aiValidationDisplayStatus: 'error',
    aiValidationFeedback:
      translate?.('tas.attachments.flag_reasons.no_analysis_available') ?? 'No analysis available',
  };
}

/** Tags to persist when no AI response landed within ANALYSIS_TIMEOUT_MS. */
export function analysisTimeoutTags(
  baseTags: Record<string, unknown> | null | undefined,
  declaredType: DocumentType
): Record<string, unknown> {
  return {
    ...(baseTags ?? {}),
    employeeSelectedType: declaredType,
    aiValidationStatus: 'flagged',
    aiValidationDisplayStatus: 'not_validated',
  };
}
